import { Link } from "react-router-dom";
import { Download, Lock, MapPin, Calendar } from "lucide-react";
import { fileUrl, fmtDate } from "@/lib/api";
import useApi, { LoadError } from "@/lib/useApi";
import useSEO from "@/hooks/useSEO";
import { PageHero, Reveal, AccessBadge, DemoTag, Empty } from "@/components/ui-lux";
import PartnerCards from "@/components/PartnerCards";
import { useAuth } from "@/context/AuthContext";

const useList = (coll) => {
  const { user } = useAuth();
  return useApi(`/content/${coll}`, [user?.id, user?.tier]);
};

export function Coaching() {
  const { data: programs, error, reload } = useList("programs");
  useSEO({ title: "Coaching", description: "Group coaching, individual coaching and transformation intensives with Todd Coconato." });
  return (
    <div data-testid="coaching-page">
      <PageHero eyebrow="Coaching" title="Guided transformation, personally." sub="Group coaching, individual coaching and immersive intensives to help you break through and build a renewed life." image="/images/todd-4707.jpg" />
      <div className="container-x space-y-24">
        {error && <LoadError onRetry={reload} />}
        <Reveal>
          <div className="card-lux p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 border-[#D4AF37]/40" data-testid="coaching-partner-cta">
            <div>
              <span className="eyebrow">Personal coaching connection</span>
              <h2 className="font-serif text-2xl text-white">Included with a Kingdom Partnership of $500 a month</h2>
              <p className="text-sm text-slate-400 mt-2">Live coaching on Zoom and email answers with resources for your situation.</p>
            </div>
            <Link to="/partnership" className="btn-gold shrink-0" data-testid="coaching-partner-btn">Become a Kingdom Partner</Link>
          </div>
        </Reveal>
        {(programs || []).map((p, i) => (
          <Reveal key={p.id}>
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`coaching-program-${p.slug}`}>
              <img src={p.image} alt="" className="w-full aspect-[4/3] object-cover object-top rounded-2xl" />
              <div>
                <span className="eyebrow">Program 0{i + 1}</span>
                <h2 className="h2">{p.title}</h2>
                <p className="lead mt-6">{p.description}</p>
                <p className="text-sm text-slate-400 mt-4 whitespace-pre-line">{p.details}</p>
                <Link to={p.cta_url || "/contact"} className="btn-gold mt-8" data-testid={`coaching-cta-${p.slug}`}>{p.cta_label || "Inquire Now"}</Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function Partnership() {
  useSEO({ title: "Partnership", description: "Partner monthly with The Transformed Mind Experience and Todd Coconato." });
  return (
    <div data-testid="partnership-page">
      <PageHero eyebrow="Partnership" title="Become a Partner." sub="Every teaching, course and resource on this site is free. Your monthly partnership keeps it that way and helps the message reach more lives." image="/images/todd-4694.jpg" />
      <div className="container-x"><PartnerCards /></div>
    </div>
  );
}

export function Resources() {
  const { data: items, error, reload } = useList("resources");
  useSEO({ title: "Go Deeper", description: "Downloadable worksheets, study guides and devotionals." });
  return (
    <div data-testid="resources-page">
      <PageHero eyebrow="Go Deeper" title="Tools for daily renewal." sub="Worksheets, study guides, devotionals and more to help you apply every teaching." />
      <div className="container-x">
        {error ? <LoadError onRetry={reload} /> : items && !items.length ? <Empty text="Resources are coming soon." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(items || []).map((r) => (
              <div key={r.id} className="card-lux overflow-hidden flex flex-col" data-testid={`resource-card-${r.slug}`}>
                <img src={r.thumbnail} alt="" className="aspect-[4/3] object-cover" />
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex gap-2 mb-3"><AccessBadge access={r.access} locked={r.locked} /><DemoTag show={r.is_demo} /></div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-accent">{r.resource_type}</p>
                  <h3 className="font-serif text-lg font-semibold text-white mt-1">{r.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-3 flex-1">{r.is_demo ? "" : r.description}</p>
                  {r.locked ? (
                    <Link to="/register" className="btn-outline !py-2.5 mt-5" data-testid={`resource-unlock-${r.slug}`}><Lock size={14} />Unlock</Link>
                  ) : (
                    <a href={fileUrl(r.file_url)} target="_blank" rel="noreferrer" className="btn-gold !py-2.5 mt-5" data-testid={`resource-download-${r.slug}`}><Download size={14} />Download</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function Events() {
  const { data: items, error, reload } = useList("events");
  useSEO({ title: "Events", description: "Live events, workshops, conferences and online trainings." });
  return (
    <div data-testid="events-page">
      <PageHero eyebrow="Events" title="Gather. Learn. Be transformed." sub="Upcoming live events, workshops, conferences and online training sessions." image="/images/todd-stadium.jpg" />
      <div className="container-x space-y-5">
        {error && <LoadError onRetry={reload} />}
        {items && !items.length && <Empty text="No upcoming events right now. Subscribe to the newsletter to hear first." />}
        {(items || []).map((e) => (
          <Reveal key={e.id}>
            <div className="card-lux grid md:grid-cols-[260px_1fr_auto] gap-6 p-4 items-center" data-testid={`event-card-${e.slug}`}>
              <img src={e.image} alt="" className="w-full aspect-video md:aspect-[4/3] object-cover rounded-lg" />
              <div>
                <div className="flex gap-2 mb-2"><span className="text-[10px] uppercase tracking-widest bg-white text-black px-2.5 py-1 rounded-full">{e.event_type}</span><DemoTag show={e.is_demo} /></div>
                <h3 className="font-serif text-2xl font-semibold tracking-tight text-white">{e.title}</h3>
                <p className="flex flex-wrap gap-5 text-sm text-slate-400 mt-3"><span className="flex items-center gap-2"><Calendar size={14} />{fmtDate(e.start_date)}</span><span className="flex items-center gap-2"><MapPin size={14} />{e.location}</span></p>
                <p className="text-sm text-slate-400 mt-3 line-clamp-2">{e.description}</p>
              </div>
              {e.registration_url ? <a href={e.registration_url} target="_blank" rel="noreferrer" className="btn-gold md:mr-4">Register</a> : <Link to="/contact" className="btn-outline md:mr-4">Get details</Link>}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
